import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import InviteUserModal from '../components/InviteUserModal';
import '../styles/TeamManagement.css';
import { ArrowLeft } from 'lucide-react';

const TeamManagement = () => {
  const { user, token, logout } = useAuth();
  const API_BASE_URL_AUTH = process.env.REACT_APP_API_BASE_URL_AUTH;
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [companies, setCompanies] = useState([]);
  const [warehouses, setWarehouses] = useState([]);

  const [activeTab, setActiveTab] = useState("team");

  useEffect(() => {
    fetchTeamMembers();
  }, []);

  const fetchTeamMembers = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL_AUTH}/Employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await response.json();
       
      setTeamMembers(data);

      setTimeout(() => setLoading(false), 500);

    } catch (err) {
      setError('Failed to load team members');
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL_AUTH}/Admit/admitted`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await response.json();
      setCompanies(data);

      setTimeout(() => setLoading(false), 500);

    } catch (err) {
      setError('Failed to load team members');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
      const cached = sessionStorage.getItem("warehouses");
  
      if (cached) {
        const parsed = JSON.parse(cached);
        const l2 = parsed.filter(w => w.mainWarehouseId !== null);
        setWarehouses(l2);
        return;
      }
  
      const fetchWarehouses = async () => {
        try {
          const res = await fetch(`${API_BASE_URL}/Warehouse`, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          });
  
          const data = await res.json();
          const parsed = JSON.parse(cached);
          const l2 = parsed.filter(w => w.mainWarehouseId !== null);
          setWarehouses(l2);
          sessionStorage.setItem("warehouses", JSON.stringify(data));
        } catch (err) {
          setError("Failed to fetch warehouses");
        }
      };
  
      fetchWarehouses();
    }, [token]);

    const getWarehouseName = (warehouseId) => {
    if (!warehouseId) return '-';
    const warehouse = warehouses.find(w => w.id === warehouseId);
    return warehouse ? warehouse.name : warehouseId;
  };

  const handleInviteUser = async (inviteData) => {
    const companyName =
      companies.find(c => c.companyId === user.companyId)?.name || "Unknown";

    try {
      const response = await fetch(`${API_BASE_URL_AUTH}/Employees/invite`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ ...inviteData, companyName })
      });

      const newUser = await response.json();

      setTeamMembers([...teamMembers, newUser]);
      setShowInviteModal(false);

      setSuccessMessage("User invited successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);

    } catch (err) {
      setError("Failed to invite user");
    }
  };

  const handleRemoveUser = async (userId) => {
    if (!window.confirm("Are you sure you want to remove this user?")) return;

    try {
      setTeamMembers(teamMembers.filter(member => member.id !== userId));
      setSuccessMessage("User removed successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      setError("Failed to remove user");
    }
  };

  const handleBackToDashboard = () => {
    window.location.href = "/dashboard";
  };

  const merchandisers = teamMembers.filter(
    m => m.role?.toLowerCase() === "merchandiser"
  );

  return (
    <div className="team-container">


      <div className="info-header">
        <div>
          <h3 style={{ color: '#2d5016', margin: 0 }}>
            {user?.fullName || `${user?.firstName} ${user?.lastName}` || user?.email}
          </h3>
          <h3 style={{ color: '#2d5016', margin: 0 }}>
            {user?.companyName}
          </h3>
          <p style={{ color: '#666', fontSize: '14px', margin: '4px 0 0 0' }}>
            {user?.email} - {user?.role}
          </p>
        </div>

        <button 
          onClick={logout}
          style={{
            background: "#ff8c00",
            color: "white",
            border: "none",
            padding: "10px 20px",
            borderRadius: "6px",
            cursor: "pointer",
            fontWeight: "600"
          }}
        >
          Logout
        </button>
      </div>

      <div className="content-container">
        <div className="team-header">
          <div>
            <h1>Team Management</h1>
            <p className="team-subtitle">Manage your company's team members</p>
          </div>

          <div className="header-actions">
            <button 
              className="back-btn"
              onClick={handleBackToDashboard}
            >
              <ArrowLeft style={{ width: 16, height: 16 }} />
              Back to Dashboard
            </button>

            <button 
              className="invite-button"
              onClick={() => setShowInviteModal(true)}
            >
              + Invite User
            </button>
          </div>
        </div>

        {error && <div className="error-banner">{error}</div>}
        {successMessage && <div className="success-banner">{successMessage}</div>}

        <div className="company-info">
          <h3>{user.companyName}</h3>
          <p>{teamMembers.length} team member{teamMembers.length !== 1 ? 's' : ''}</p>
        </div>

        <div className="tab-buttons">
          <button 
            className={`tab-btn ${activeTab === "team" ? "active" : ""}`}
            onClick={() => setActiveTab("team")}
          >
            Team Members
          </button>

          <button 
            className={`tab-btn ${activeTab === "merch" ? "active" : ""}`}
            onClick={() => setActiveTab("merch")}
          >
            Merchandisers
          </button>
        </div>

        <div className="team-table-container">

          {activeTab === "team" && (
            <table className="team-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Joined</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>

              {loading ? (
                <tbody><tr><td colSpan="5" style={{ textAlign: "center" }}>Loading...</td></tr></tbody>
              ) : (
                <tbody>
                  {teamMembers.map(member => (
                    <tr key={member.id}>
                      <td>{member.email}</td>
                      <td>
                        <span className={`role-badge ${member.role?.toLowerCase()}`}>{member.role}</span>
                      </td>
                      <td>{member.createdAt}</td>
                      <td><span className="status-active">{member.status}</span></td>
                      <td>
                        {member.id !== user.userId ? (
                          <button className="remove-button" onClick={() => handleRemoveUser(member.id)}>
                            Remove
                          </button>
                        ) : (
                          <span className="current-user-badge">You</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              )}
            </table>
          )}

          {/* Merchandisers tab */}
          {activeTab === "merch" && (
            <table className="team-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Warehouse</th>
                  <th>Joined</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>

              {loading ? (
                <tbody><tr><td colSpan="4" style={{ textAlign: "center" }}>Loading...</td></tr></tbody>
              ) : merchandisers.length > 0 ? (
                <tbody>
                  {merchandisers.map(m => (
                    <tr key={m.id}>
                      <td>{m.email}</td>
                      <td>{getWarehouseName(m.warehouseId) || "—"}</td>
                      <td>{m.createdAt}</td>
                      <td><span className="status-active">{m.status}</span></td>
                      <td>
                        {m.id !== user.userId ? (
                          <button className="remove-button" onClick={() => handleRemoveUser(m.id)}>
                            Remove
                          </button>
                        ) : (
                          <span className="current-user-badge">You</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              ) : (
                <tbody><tr><td colSpan="4" className="no-members">No merchandisers found.</td></tr></tbody>
              )}
            </table>
          )}

        </div>
      </div>

      {showInviteModal && (
        <InviteUserModal
          onClose={() => setShowInviteModal(false)}
          onInvite={handleInviteUser}
        />
      )}
    </div>
  );
};

export default TeamManagement;
